import { Plus, Edit, Trash2, Calendar, User, Loader2, Languages } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { ChildAvatar } from '../ChildAvatar'
import { DashboardLayout } from '../DashboardLayout'
import type { Child, Gender } from '@/types/dashboard.types'
import { ChildAPI, AuthAPI } from '@/services/api'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'

interface ChildFormData {
  firstName: string
  lastName: string
  dateOfBirth: string
  gender: Gender
}

const initialFormData: ChildFormData = {
  firstName: '',
  lastName: '',
  dateOfBirth: '',
  gender: 'MALE' as Gender,
}

const toIsoDateStartOfDay = (dateValue: string): string => {
  return new Date(`${dateValue}T00:00:00`).toISOString()
}

export const ChildManagement = () => {
  const [user, setUser] = useState<any>(null)
  const [children, setChildren] = useState<Child[]>([])
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false)
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [selectedChild, setSelectedChild] = useState<Child | null>(null)
  const [formData, setFormData] = useState<ChildFormData>(initialFormData)
  const [showEnglish, setShowEnglish] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [isAddingChild, setIsAddingChild] = useState(false)
  const [isEditingChild, setIsEditingChild] = useState(false)
  const [isDeletingChild, setIsDeletingChild] = useState(false)

  const fetchChildren = async () => {
    try {
      const data = await ChildAPI.getChildren()
      setChildren(data)
    } catch (error) {
      console.error('Failed to fetch children', error)
    }
  }

  useEffect(() => {
    const init = async () => {
      try {
        const u = await AuthAPI.me()
        setUser(u.user)
        await fetchChildren()
      } catch (error) {
        console.error('Failed init', error)
      } finally {
        setIsLoading(false)
      }
    }
    init()
  }, [])

  // Calculate age from date of birth
  const calculateAge = (dateOfBirth: string): { years: number; months: number } => {
    const birthDate = new Date(dateOfBirth)
    const today = new Date()

    let years = today.getFullYear() - birthDate.getFullYear()
    let months = today.getMonth() - birthDate.getMonth()

    if (months < 0) {
      years--
      months += 12
    }

    return { years, months }
  }

  const handleAddChild = async () => {
    try {
      if (!formData.dateOfBirth) {
        toast.error('Date of birth is required')
        return
      }

      setIsAddingChild(true)

      await ChildAPI.createChild({
        firstName: formData.firstName,
        lastName: formData.lastName,
        dateOfBirth: toIsoDateStartOfDay(formData.dateOfBirth),
        gender: formData.gender,
      })
      await fetchChildren()
      setFormData(initialFormData)
      setIsAddDialogOpen(false)
      toast.success(`Child ${formData.firstName} added successfully!`)
    } catch (error: any) {
      console.error('Error adding child', error)
      toast.error(error.response?.data?.message || 'Failed to add child')
    } finally {
      setIsAddingChild(false)
    }
  }

  const handleEditChild = async () => {
    if (!selectedChild) return

    try {
      if (!formData.dateOfBirth) {
        toast.error('Date of birth is required')
        return
      }

      setIsEditingChild(true)

      await ChildAPI.updateChild(selectedChild.id, {
        firstName: formData.firstName,
        lastName: formData.lastName,
        dateOfBirth: toIsoDateStartOfDay(formData.dateOfBirth),
        gender: formData.gender,
      })
      await fetchChildren()
      setSelectedChild(null)
      setFormData(initialFormData)
      setIsEditDialogOpen(false)
      toast.success(`Child ${formData.firstName} updated successfully!`)
    } catch (error: any) {
      console.error('Error updating child', error)
      toast.error(error.response?.data?.message || 'Failed to update child')
    } finally {
      setIsEditingChild(false)
    }
  }

  const handleDeleteChild = async () => {
    if (!selectedChild) return

    try {
      setIsDeletingChild(true)
      await ChildAPI.deleteChild(selectedChild.id)
      await fetchChildren()
      setSelectedChild(null)
      setIsDeleteDialogOpen(false)
      toast.success(`Child ${selectedChild.firstName} removed successfully!`)
    } catch (error) {
      console.error('Error deleting child', error)
      toast.error('Failed to delete child')
    } finally {
      setIsDeletingChild(false)
    }
  }

  const handleOpenEdit = (child: Child) => {
    setSelectedChild(child)
    setFormData({
      firstName: child.firstName || '',
      lastName: child.lastName || '',
      dateOfBirth: child.dateOfBirth ? new Date(child.dateOfBirth).toISOString().slice(0, 10) : '',
      gender: child.gender as Gender,
    })
    setIsEditDialogOpen(true)
  }

  const handleOpenDelete = (child: Child) => {
    setSelectedChild(child)
    setIsDeleteDialogOpen(true)
  }

  const activeChildren = children.filter((child) => child.isActive !== false)

  if (isLoading || !user) {
    return (
      <DashboardLayout user={user || { firstName: 'User', lastName: '', email: '', role: 'PARENT' }}>
        <div className="flex h-[50vh] items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    )
  }

  return (
    <DashboardLayout user={user}>
      <div className="space-y-6 animate-in fade-in duration-500">
        {/* Header */}
        <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
          <div className="animate-in slide-in-from-left duration-700 min-w-0">
            <h1 className="text-3xl font-bold bg-gradient-to-r from-teal-600 to-teal-800 bg-clip-text text-transparent">
              Children Management
            </h1>
            <p className="text-muted-foreground mt-2">
              Manage your children's profiles and information
            </p>
            {showEnglish && (
              <p className="text-sm text-blue-600 mt-1 italic">
                English: Manage your children's profiles and information
              </p>
            )}
          </div>

          <div className="flex flex-wrap gap-2 animate-in slide-in-from-right duration-700 shrink-0">
            <Button
              variant={showEnglish ? "default" : "outline"}
              onClick={() => setShowEnglish(!showEnglish)}
              className="gap-2"
              type="button"
              aria-pressed={showEnglish}
            >
              <Languages className="h-4 w-4 shrink-0" aria-hidden />
              {showEnglish ? 'Filipino' : 'English'}
            </Button>

            {/* Add Child Dialog */}
            <Dialog
              open={isAddDialogOpen}
              onOpenChange={(open) => {
                if (isAddingChild) return
                setIsAddDialogOpen(open)
                if (!open) {
                  setFormData(initialFormData)
                }
              }}
            >
              <DialogTrigger asChild>
                <Button size="lg" variant="default" className="gap-2" aria-label="Add new child">
                  <Plus className="h-5 w-5" aria-hidden="true" />
                  Add Child
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Add New Child</DialogTitle>
                  <DialogDescription>
                    Add a new child profile for speech assessment. Supported age is automatically
                    validated based on the active questionnaire range configured in the system.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label htmlFor="add-firstName">First Name</Label>
                    <Input
                      id="add-firstName"
                      value={formData.firstName}
                      onChange={(e) =>
                        setFormData({ ...formData, firstName: e.target.value })
                      }
                      placeholder="Enter first name"
                      aria-label="Child's first name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="add-lastName">Last Name</Label>
                    <Input
                      id="add-lastName"
                      value={formData.lastName}
                      onChange={(e) =>
                        setFormData({ ...formData, lastName: e.target.value })
                      }
                      placeholder="Enter last name"
                      aria-label="Child's last name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="add-dateOfBirth">Date of Birth</Label>
                    <Input
                      id="add-dateOfBirth"
                      type="date"
                      value={formData.dateOfBirth}
                      onChange={(e) =>
                        setFormData({ ...formData, dateOfBirth: e.target.value })
                      }
                      aria-label="Child's date of birth"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="add-gender">Gender</Label>
                    <Select
                      value={formData.gender}
                      onValueChange={(value) =>
                        setFormData({ ...formData, gender: value as ChildFormData['gender'] })
                      }
                    >
                      <SelectTrigger id="add-gender" aria-label="Child's gender">
                        <SelectValue placeholder="Select gender" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="MALE">Male</SelectItem>
                        <SelectItem value="FEMALE">Female</SelectItem>
                        <SelectItem value="OTHER">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <DialogFooter>
                  <Button
                    variant="outline"
                    onClick={() => setIsAddDialogOpen(false)}
                    disabled={isAddingChild}
                  >
                    Cancel
                  </Button>
                  <Button onClick={handleAddChild} aria-label="Save new child" disabled={isAddingChild}>
                    {isAddingChild ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Adding...
                      </>
                    ) : (
                      'Add Child'
                    )}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Children List */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 animate-in slide-in-from-top duration-700">
          {activeChildren.map((child) => {
            const age = calculateAge(child.dateOfBirth)

            return (
              <Card key={child.id} className="border-transparent bg-gradient-to-br from-primary/10 to-accent/40 backdrop-blur-sm hover:border-border hover:shadow-lg transition-all animate-in slide-in-from-bottom duration-700">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <ChildAvatar gender={child.gender} size="md" />
                      <div>
                        <CardTitle className="text-lg">
                          {child.firstName} {child.lastName}
                        </CardTitle>
                        <p className="text-sm text-muted-foreground">
                          {age.years} years, {age.months} months
                        </p>
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Calendar className="h-4 w-4" aria-hidden="true" />
                      <span>
                        {new Date(child.dateOfBirth).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                        })}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <User className="h-4 w-4" aria-hidden="true" />
                      <span className="capitalize">{child.gender.toLowerCase()}</span>
                    </div>
                  </div>

                  <div className="flex gap-2 mt-4">
                    <Button
                      variant="outline"
                      size="default"
                      className="flex-1 gap-2"
                      onClick={() => handleOpenEdit(child)}
                      aria-label={`Edit ${child.firstName}'s profile`}
                    >
                      <Edit className="h-4 w-4" aria-hidden="true" />
                      Edit
                    </Button>
                    <Button
                      variant="destructive"
                      size="default"
                      className="flex-1 gap-2"
                      onClick={() => handleOpenDelete(child)}
                      aria-label={`Remove ${child.firstName}'s profile`}
                    >
                      <Trash2 className="h-4 w-4" aria-hidden="true" />
                      Remove
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )
          })}
        </div>

        {/* Edit Child Dialog */}
        <Dialog
          open={isEditDialogOpen}
          onOpenChange={(open) => {
            if (isEditingChild) return
            setIsEditDialogOpen(open)
            if (!open) {
              setSelectedChild(null)
              setFormData(initialFormData)
            }
          }}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit Child Profile</DialogTitle>
              <DialogDescription>
                Update the child's information below.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="edit-firstName">First Name</Label>
                <Input
                  id="edit-firstName"
                  value={formData.firstName}
                  onChange={(e) =>
                    setFormData({ ...formData, firstName: e.target.value })
                  }
                  placeholder="Enter first name"
                  aria-label="Child's first name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-lastName">Last Name</Label>
                <Input
                  id="edit-lastName"
                  value={formData.lastName}
                  onChange={(e) =>
                    setFormData({ ...formData, lastName: e.target.value })
                  }
                  placeholder="Enter last name"
                  aria-label="Child's last name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-dateOfBirth">Date of Birth</Label>
                <Input
                  id="edit-dateOfBirth"
                  type="date"
                  value={formData.dateOfBirth}
                  onChange={(e) =>
                    setFormData({ ...formData, dateOfBirth: e.target.value })
                  }
                  aria-label="Child's date of birth"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-gender">Gender</Label>
                <Select
                  value={formData.gender}
                  onValueChange={(value) =>
                    setFormData({ ...formData, gender: value as ChildFormData['gender'] })
                  }
                >
                  <SelectTrigger id="edit-gender" aria-label="Child's gender">
                    <SelectValue placeholder="Select gender" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="MALE">Male</SelectItem>
                    <SelectItem value="FEMALE">Female</SelectItem>
                    <SelectItem value="OTHER">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setIsEditDialogOpen(false)
                  setSelectedChild(null)
                  setFormData(initialFormData)
                }}
                disabled={isEditingChild}
              >
                Cancel
              </Button>
              <Button onClick={handleEditChild} aria-label="Save changes" disabled={isEditingChild}>
                {isEditingChild ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Save Changes'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={isDeleteDialogOpen}
          onOpenChange={(open) => {
            if (isDeletingChild) return
            setIsDeleteDialogOpen(open)
            if (!open) {
              setSelectedChild(null)
            }
          }}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Remove Child Profile</DialogTitle>
              <DialogDescription>
                Are you sure you want to remove {selectedChild?.firstName}'s profile? This
                will hide the profile but preserve assessment history.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setIsDeleteDialogOpen(false)
                  setSelectedChild(null)
                }}
                disabled={isDeletingChild}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleDeleteChild}
                aria-label="Confirm removal"
                disabled={isDeletingChild}
              >
                {isDeletingChild ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Removing...
                  </>
                ) : (
                  'Remove Profile'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  )
}
